import React, { useRef } from 'react'
import styled from 'styled-components'
import Masonry from 'react-responsive-masonry'

import { breakpoints } from 'styles/media'
import useSize from 'hooks/useSize'

import MediaWrapper from '../cards/shared/MediaWrapper'
import LazyImage from '../display/LazyImage'

const LARGE_GUTTER = 30

const Outer = styled.div<{ $gutter?: number }>`
  padding-top: ${({ $gutter = LARGE_GUTTER }) => $gutter}px;

  & > div:first-child {
    /* targetting column container */
    position: relative;

    /* targetting all columns */
    & > div {
      position: relative;
      /* tagging important because react-responsive-masonry applies inline style that override this property */
      place-content: stretch flex-end !important;
    }

    & > div:nth-child(even) {
      /* targetting even-numbered columns */
      bottom: ${({ $gutter = LARGE_GUTTER }) => $gutter}px;
    }
  }
`

type MasonryImageProps = {
  large: string
  medium: string
  portrait: boolean
  preload?: boolean
  altTitle?: string
}

const MasonryImage = ({
  large,
  medium,
  portrait,
  preload,
  altTitle,
}: MasonryImageProps) => {
  return (
    <MediaWrapper portrait={portrait} rounded>
      <LazyImage
        style={{
          position: 'absolute',
          right: 0,
          bottom: 0,
          left: 0,
          top: 0,
        }}
        key={large}
        data-src={large}
        /*
          The actual width values vary image-to-image.
          Here we set the width value conservatively, so the browser is more likely to fetch the more high-res one,
          but there is still some allowance for it to optimise when the image renders small on screen.
        */
        data-srcset={`
          ${medium} 600w,
          ${large} 1000w,
        `}
        preload={preload}
        rounded
        alt={altTitle}
      />
    </MediaWrapper>
  )
}

const columnsForWidth = (width: number) => {
  if (width > 900) {
    return 3
  }

  if (width > breakpoints.phone) {
    return 2
  }

  return 1
}

type Images = Array<{ large: string; medium: string }>

const shouldBeLandscape = (index: number, columnsCount: number) => {
  if (columnsCount === 3) {
    return index % 2 !== 0 // every second one
  }
  if (columnsCount === 2) {
    return index % 4 >= 2 // alteranting short/tall sets of 2 (one in either column), with first as tall (hence +1)
  }

  return false
}

type ColumnsCount = 1 | 2 | 3
type ImagesPerColumn = {
  [key in ColumnsCount]: number
}

const sliceImagesToFit = (
  images: Images,
  imagesPerColumn: ImagesPerColumn | undefined,
  columnsCount: ColumnsCount | undefined | 0
) => {
  if (!columnsCount || !imagesPerColumn) {
    return images
  }

  const limitPerColumn = imagesPerColumn[columnsCount]
  return images.slice(0, limitPerColumn * columnsCount)
}

type MasonryImagesProps = {
  images: Images
  imagesPerColumn?: ImagesPerColumn
  gutter?: number
  style?: React.CSSProperties
}

/**
 * Pass a prop to set the number of images per column for each column breakpoint.
 */

const MasonryImages: React.FC<MasonryImagesProps> = ({
  images: initialImages,
  imagesPerColumn,
  gutter = LARGE_GUTTER,
  style,
}) => {
  const outerRef = useRef<HTMLDivElement>()
  const outerSize = useSize(outerRef)

  const columnsCount = outerSize?.width && columnsForWidth(outerSize.width)

  const images = sliceImagesToFit(initialImages, imagesPerColumn, columnsCount)

  return (
    <Outer ref={outerRef} style={style}>
      {columnsCount && (
        <Masonry columnsCount={columnsCount} gutter={`${gutter.toString()}px`}>
          {images.map(({ large, medium }, index) => {
            const landscape = shouldBeLandscape(index + 1, columnsCount)
            return (
              <MasonryImage
                key={large}
                large={large}
                medium={medium}
                portrait={!landscape}
              />
            )
          })}
        </Masonry>
      )}
    </Outer>
  )
}

type FixedColumnsMasonryProps = Pick<
  MasonryImagesProps,
  'images' | 'imagesPerColumn' | 'gutter' | 'style'
> & {
  columnsCount: ColumnsCount | 0 | undefined
  preload?: boolean
}

export const FixedColumnsMasonry = ({
  images: initialImages,
  columnsCount,
  imagesPerColumn,
  gutter = LARGE_GUTTER,
  style,
  preload = false,
}: FixedColumnsMasonryProps) => {
  const images = sliceImagesToFit(initialImages, imagesPerColumn, columnsCount)
  return (
    <Outer style={style} $gutter={gutter}>
      <Masonry columnsCount={columnsCount} gutter={`${gutter.toString()}px`}>
        {images.map(({ large, medium }, index) => {
          const landscape = shouldBeLandscape(index + 1, columnsCount)
          return (
            <MasonryImage
              key={large}
              large={large}
              medium={medium}
              portrait={!landscape}
              preload={preload}
              altTitle={`Masonry image ${index + 1}`}
            />
          )
        })}
      </Masonry>
    </Outer>
  )
}

export default MasonryImages
